import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth, useCompare } from "../App";
import InquiryModal from "../components/InquiryModal";
import {
  MapPin,
  ShieldCheck,
  Building2,
  Maximize,
  Calendar,
  Star,
  Heart,
  GitCompare,
  Phone,
  Video,
  Tag,
  ChevronLeft,
  Check,
  X,
  ExternalLink,
  Calculator,
  Share2,
  Home,
  Users,
  Car,
  Dumbbell,
  Waves,
  TreePine,
  Shield,
  Wifi,
  Zap,
  Layers,
  FileText,
  Download,
  HelpCircle,
  Eye,
  ChevronDown,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { toast } from "sonner";

const formatPrice = (price) => {
  if (price >= 10000000) {
    return `₹${(price / 10000000).toFixed(2)} Cr`;
  } else if (price >= 100000) {
    return `₹${(price / 100000).toFixed(2)} L`;
  }
  return `₹${price.toLocaleString("en-IN")}`;
};

const getAmenityIcon = (amenity) => {
  const lower = amenity.toLowerCase();
  if (lower.includes("pool") || lower.includes("swim")) return Waves;
  if (lower.includes("gym") || lower.includes("fitness")) return Dumbbell;
  if (lower.includes("parking") || lower.includes("car")) return Car;
  if (lower.includes("garden") || lower.includes("park") || lower.includes("green")) return TreePine;
  if (lower.includes("security") || lower.includes("guard")) return Shield;
  if (lower.includes("wifi") || lower.includes("internet")) return Wifi;
  if (lower.includes("power") || lower.includes("backup")) return Zap;
  if (lower.includes("club")) return Users;
  return Home;
};

const PropertyDetailPage = () => {
  const { propertyId } = useParams();
  const { user, token } = useAuth();
  const { addToCompare, isInCompare, removeFromCompare } = useCompare();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);
  const [inquiryModal, setInquiryModal] = useState({ open: false, type: "callback" });
  const [emiData, setEmiData] = useState({
    loanAmount: 0,
    interestRate: 8.5,
    tenure: 240,
    emi: 0,
    totalPayment: 0,
    totalInterest: 0,
  });

  const inCompare = property && isInCompare(property.property_id);

  useEffect(() => {
    const fetchProperty = async () => {
      try {
        const response = await axios.get(`${API}/properties/${propertyId}`);
        setProperty(response.data);
        // Initialize EMI calculation with average price
        const avgPrice = (response.data.price_min + response.data.price_max) / 2;
        const loanAmount = avgPrice * 0.8; // 80% loan
        calculateEMI(loanAmount, 8.5, 240);
      } catch (error) {
        console.error("Error fetching property:", error);
        toast.error("Failed to load property");
      } finally {
        setLoading(false);
      }
    };
    fetchProperty();
  }, [propertyId]);

  const calculateEMI = (P, rate, months) => {
    const r = rate / 12 / 100;
    const n = months;
    let emi;
    if (r === 0) {
      emi = P / n;
    } else {
      emi = (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    }
    const totalPayment = emi * n;
    const totalInterest = totalPayment - P;

    setEmiData({
      loanAmount: P,
      interestRate: rate,
      tenure: months,
      emi: Math.round(emi),
      totalPayment: Math.round(totalPayment),
      totalInterest: Math.round(totalInterest),
    });
  };

  const handleSave = async () => {
    if (!user) {
      toast.error("Please login to save properties");
      return;
    }

    try {
      await axios.post(
        `${API}/saved-properties/${property.property_id}`,
        {},
        {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );
      toast.success("Property saved!");
    } catch (error) {
      if (error.response?.status === 400) {
        toast.info("Property already saved");
      } else {
        toast.error("Failed to save property");
      }
    }
  };

  const handleCompare = () => {
    if (inCompare) {
      removeFromCompare(property.property_id);
    } else {
      addToCompare(property);
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: property.name,
        text: `Check out ${property.name} - ${formatPrice(property.price_min)} onwards`,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied to clipboard!");
    }
  };

  const getCategoryLabel = (category) => {
    switch (category) {
      case "new_launch":
        return "New Launch";
      case "under_construction":
        return "Under Construction";
      case "ready_to_move":
        return "Ready to Move";
      case "upcoming":
        return "Upcoming Project";
      default:
        return category;
    }
  };

  const getRERALink = (state) => {
    const links = {
      Maharashtra: "https://maharerait.mahaonline.gov.in",
      Karnataka: "https://rera.karnataka.gov.in",
      Telangana: "https://rera.telangana.gov.in",
      "Tamil Nadu": "https://tnrera.in",
      Haryana: "https://haryanarera.gov.in",
    };
    return links[state] || "https://rera.gov.in";
  };

  // Generate unit configurations based on property data
  const getUnitConfigurations = () => {
    if (!property) return [];
    
    const configs = property.configurations.map((config, index) => {
      const baseArea = property.carpet_area_min + (index * 200);
      const basePrice = property.price_min + (index * 2000000);
      return {
        type: config,
        carpet_area: `${baseArea} - ${baseArea + 300} sq.ft`,
        price: `${formatPrice(basePrice)} - ${formatPrice(basePrice + 3000000)}`,
        floor_plan: property.floor_plans?.[0] || null
      };
    });
    return configs;
  };

  // Property specifications
  const getSpecifications = () => ({
    floor: [
      { label: "Living/Dining", value: "Vitrified tiles (2x2 ft)" },
      { label: "Bedrooms", value: "Vitrified tiles / Wooden flooring" },
      { label: "Kitchen", value: "Anti-skid ceramic tiles" },
      { label: "Bathrooms", value: "Anti-skid ceramic tiles" },
      { label: "Balcony", value: "Rustic finish tiles" },
    ],
    fittings: [
      { label: "Kitchen", value: "Modular kitchen with granite platform" },
      { label: "CP Fittings", value: "Jaguar/Grohe or equivalent" },
      { label: "Sanitary Ware", value: "Kohler/Duravit or equivalent" },
      { label: "Electrical", value: "Anchor/Legrand modular switches" },
      { label: "Doors", value: "Teak wood frame with flush doors" },
    ],
    wall_ceiling: [
      { label: "Internal Walls", value: "Gypsum plastered with POP finish" },
      { label: "External Walls", value: "Weather-proof exterior paint" },
      { label: "Ceiling", value: "POP punning with OBD paint" },
      { label: "Kitchen", value: "Ceramic tile dado up to 2 ft" },
      { label: "Bathrooms", value: "Ceramic tile dado up to 7 ft" },
    ],
  });

  // Project FAQs
  const getProjectFAQs = () => [
    {
      question: `What is the RERA registration number for ${property?.name}?`,
      answer: `The RERA registration number is ${property?.rera_id}. You can verify this on the ${property?.rera_state} RERA portal.`
    },
    {
      question: "What are the available configurations?",
      answer: `${property?.name} offers ${property?.configurations?.join(", ")} configurations with carpet areas ranging from ${property?.carpet_area_min} to ${property?.carpet_area_max} sq.ft.`
    },
    {
      question: "What is the possession timeline?",
      answer: `The expected possession date is ${property?.possession_date}. Please contact our team for the latest updates.`
    },
    {
      question: "Are there any loan tie-ups available?",
      answer: "Yes, we have tie-ups with leading banks including SBI, HDFC, ICICI, and Axis Bank. Our team can assist you with the loan application process."
    },
    {
      question: "What amenities are included?",
      answer: `${property?.name} offers ${property?.amenities?.length} amenities including ${property?.amenities?.slice(0, 5).join(", ")} and more.`
    },
    {
      question: "Is there any maintenance deposit required?",
      answer: "Yes, typically 2 years of maintenance deposit is collected at the time of possession. The exact amount will be shared by our team."
    },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-emerald-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-display text-2xl text-stone-900 mb-4">Property Not Found</h1>
          <Link to="/search" className="btn-primary">
            Browse Properties
          </Link>
        </div>
      </div>
    );
  }

  const unitConfigs = getUnitConfigurations();
  const specifications = getSpecifications();
  const projectFAQs = getProjectFAQs();

  return (
    <div className="min-h-screen pt-20 bg-stone-50" data-testid="property-detail-page">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-stone-100">
        <div className="section-container py-4">
          <div className="flex items-center gap-2 text-sm">
            <Link to="/" className="text-stone-500 hover:text-emerald-900">
              Home
            </Link>
            <span className="text-stone-300">/</span>
            <Link to={`/city/${property.city.toLowerCase().replace(" ", "-")}`} className="text-stone-500 hover:text-emerald-900">
              {property.city}
            </Link>
            <span className="text-stone-300">/</span>
            <span className="text-stone-900">{property.name}</span>
          </div>
        </div>
      </div>

      {/* Image Gallery */}
      <div className="bg-white">
        <div className="section-container py-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Main Image */}
            <div className="lg:col-span-2 relative aspect-[16/10] overflow-hidden rounded-sm">
              <img
                src={property.images?.[activeImage] || "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=800"}
                alt={property.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute top-4 left-4 flex items-center gap-2">
                <span className="bg-amber-500 text-white text-xs px-3 py-1 uppercase tracking-wider font-medium">
                  {getCategoryLabel(property.category)}
                </span>
                {property.featured && (
                  <span className="bg-emerald-600 text-white text-xs px-3 py-1 uppercase tracking-wider font-medium">
                    Featured
                  </span>
                )}
              </div>
            </div>

            {/* Thumbnails */}
            <div className="grid grid-cols-2 gap-4">
              {(property.images?.slice(0, 4) || []).map((img, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImage(i)}
                  className={`relative aspect-[4/3] overflow-hidden rounded-sm ${
                    activeImage === i ? "ring-2 ring-emerald-900" : ""
                  }`}
                >
                  <img src={img} alt={`${property.name} ${i + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="section-container py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-8">
            {/* Header */}
            <div className="bg-white p-6 rounded-sm shadow-layered" data-testid="property-header">
              <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                <div>
                  <h1 className="font-display text-2xl md:text-3xl text-stone-900 mb-2">{property.name}</h1>
                  <p className="text-stone-500 flex items-center gap-1">
                    <MapPin className="w-4 h-4" strokeWidth={1.5} />
                    {property.address}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleSave}
                    className="p-2 border border-stone-200 hover:border-stone-300 rounded-sm transition-colors"
                    data-testid="save-property-btn"
                  >
                    <Heart className="w-5 h-5 text-stone-600" strokeWidth={1.5} />
                  </button>
                  <button
                    onClick={handleCompare}
                    className={`p-2 border rounded-sm transition-colors ${
                      inCompare
                        ? "bg-amber-500 border-amber-500 text-white"
                        : "border-stone-200 hover:border-stone-300"
                    }`}
                    data-testid="compare-property-btn"
                  >
                    <GitCompare className={`w-5 h-5 ${inCompare ? "" : "text-stone-600"}`} strokeWidth={1.5} />
                  </button>
                  <button
                    onClick={handleShare}
                    className="p-2 border border-stone-200 hover:border-stone-300 rounded-sm transition-colors"
                    data-testid="share-property-btn"
                  >
                    <Share2 className="w-5 h-5 text-stone-600" strokeWidth={1.5} />
                  </button>
                </div>
              </div>

              {/* Key Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 py-4 border-y border-stone-100">
                <div>
                  <p className="text-xs uppercase tracking-widest text-stone-500 mb-1">Price Range</p>
                  <p className="font-mono text-lg text-stone-900">
                    {formatPrice(property.price_min)} - {formatPrice(property.price_max)}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-stone-500 mb-1">Configuration</p>
                  <p className="font-medium text-stone-900">{property.configurations.join(", ")}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-stone-500 mb-1">Carpet Area</p>
                  <p className="font-mono text-stone-900">
                    {property.carpet_area_min} - {property.carpet_area_max} sq.ft
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-stone-500 mb-1">Possession</p>
                  <p className="font-medium text-stone-900">{property.possession_date}</p>
                </div>
              </div>

              {/* Rating */}
              <div className="flex items-center gap-4 mt-4">
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star
                      key={i}
                      className={`w-5 h-5 ${i <= Math.round(property.rating) ? "text-amber-500" : "text-stone-200"}`}
                      strokeWidth={1.5}
                      fill={i <= Math.round(property.rating) ? "currentColor" : "none"}
                    />
                  ))}
                </div>
                <span className="font-mono text-lg text-stone-900">{property.rating}</span>
                <span className="text-stone-500 text-sm">({property.reviews_count} reviews)</span>
              </div>
            </div>

            {/* Project Details Summary */}
            <div className="bg-white p-6 rounded-sm shadow-layered" data-testid="project-details">
              <h3 className="font-display text-xl text-stone-900 mb-4">Project Details</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-stone-50 rounded-sm">
                  <Layers className="w-5 h-5 text-emerald-700 mb-2" strokeWidth={1.5} />
                  <p className="text-xs uppercase tracking-widest text-stone-500 mb-1">No. of Storeys</p>
                  <p className="font-medium text-stone-900">{property.property_type === "Apartment" ? "G + 25 Floors" : "Ground"}</p>
                </div>
                <div className="p-4 bg-stone-50 rounded-sm">
                  <Maximize className="w-5 h-5 text-emerald-700 mb-2" strokeWidth={1.5} />
                  <p className="text-xs uppercase tracking-widest text-stone-500 mb-1">Project Area</p>
                  <p className="font-medium text-stone-900">{Math.round(property.total_units / 50)} Acres</p>
                </div>
                <div className="p-4 bg-stone-50 rounded-sm">
                  <Building2 className="w-5 h-5 text-emerald-700 mb-2" strokeWidth={1.5} />
                  <p className="text-xs uppercase tracking-widest text-stone-500 mb-1">Total Units</p>
                  <p className="font-medium text-stone-900">{property.total_units} Units</p>
                </div>
                <div className="p-4 bg-stone-50 rounded-sm">
                  <Home className="w-5 h-5 text-emerald-700 mb-2" strokeWidth={1.5} />
                  <p className="text-xs uppercase tracking-widest text-stone-500 mb-1">Available</p>
                  <p className="font-medium text-stone-900">{property.available_units} Units</p>
                </div>
              </div>
            </div>

            {/* RERA Disclosure */}
            <div className="bg-emerald-50 border border-emerald-100 p-6 rounded-sm" data-testid="rera-disclosure">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-emerald-600 rounded-sm flex items-center justify-center flex-shrink-0">
                  <ShieldCheck className="w-6 h-6 text-white" strokeWidth={1.5} />
                </div>
                <div className="flex-1">
                  <h3 className="font-display text-lg text-emerald-900 mb-1">RERA Verified Project</h3>
                  <p className="font-mono text-sm text-emerald-700 mb-2">RERA ID: {property.rera_id}</p>
                  <p className="text-sm text-emerald-700 mb-3">
                    This project is registered under {property.rera_state} RERA. All project details are verified directly from the state authority.
                  </p>
                  <a
                    href={getRERALink(property.rera_state)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-emerald-700 hover:text-emerald-900 font-medium"
                  >
                    Verify on {property.rera_state} RERA <ExternalLink className="w-4 h-4" strokeWidth={1.5} />
                  </a>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <Tabs defaultValue="overview" className="bg-white rounded-sm shadow-layered">
              <TabsList className="w-full justify-start border-b border-stone-100 rounded-none bg-transparent p-0 flex-wrap">
                <TabsTrigger
                  value="overview"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-emerald-900 data-[state=active]:bg-transparent px-4 py-4 text-sm"
                >
                  Overview
                </TabsTrigger>
                <TabsTrigger
                  value="pricing"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-emerald-900 data-[state=active]:bg-transparent px-4 py-4 text-sm"
                >
                  Pricing & Floor Plans
                </TabsTrigger>
                <TabsTrigger
                  value="specifications"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-emerald-900 data-[state=active]:bg-transparent px-4 py-4 text-sm"
                >
                  Specifications
                </TabsTrigger>
                <TabsTrigger
                  value="amenities"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-emerald-900 data-[state=active]:bg-transparent px-4 py-4 text-sm"
                >
                  Amenities
                </TabsTrigger>
                <TabsTrigger
                  value="brochure"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-emerald-900 data-[state=active]:bg-transparent px-4 py-4 text-sm"
                >
                  Brochure
                </TabsTrigger>
                <TabsTrigger
                  value="pros-cons"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-emerald-900 data-[state=active]:bg-transparent px-4 py-4 text-sm"
                >
                  Pros & Cons
                </TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="p-6">
                <h3 className="font-display text-xl text-stone-900 mb-4">About This Project</h3>
                <p className="text-stone-600 leading-relaxed mb-6">{property.description}</p>

                <h4 className="font-medium text-stone-900 mb-3">Highlights</h4>
                <div className="flex flex-wrap gap-2 mb-6">
                  {property.highlights.map((highlight, i) => (
                    <span key={i} className="bg-stone-100 text-stone-700 text-sm px-3 py-1 rounded-sm">
                      {highlight}
                    </span>
                  ))}
                </div>

                {/* Builder Details */}
                <div className="border-t border-stone-100 pt-6 mt-6">
                  <h4 className="font-medium text-stone-900 mb-4">Builder Details</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="p-4 bg-stone-50 rounded-sm">
                      <p className="text-xs uppercase tracking-widest text-stone-500 mb-1">Builder Name</p>
                      <p className="font-medium text-stone-900">{property.builder}</p>
                    </div>
                    <div className="p-4 bg-stone-50 rounded-sm">
                      <p className="text-xs uppercase tracking-widest text-stone-500 mb-1">Property Type</p>
                      <p className="font-medium text-stone-900">{property.property_type}</p>
                    </div>
                    <div className="p-4 bg-stone-50 rounded-sm">
                      <p className="text-xs uppercase tracking-widest text-stone-500 mb-1">Location</p>
                      <p className="font-medium text-stone-900">{property.locality}, {property.city}</p>
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="pricing" className="p-6">
                <h3 className="font-display text-xl text-stone-900 mb-4">Pricing & Floor Plans</h3>
                <p className="text-stone-500 text-sm mb-6">Price range: {formatPrice(property.price_min)} - {formatPrice(property.price_max)}</p>
                
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-stone-200">
                        <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-stone-500 font-medium">Type</th>
                        <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-stone-500 font-medium">Carpet Area</th>
                        <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-stone-500 font-medium">Price</th>
                        <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-stone-500 font-medium">Floor Plan</th>
                      </tr>
                    </thead>
                    <tbody>
                      {unitConfigs.map((config, i) => (
                        <tr key={i} className="border-b border-stone-100">
                          <td className="py-4 px-4 font-medium text-stone-900">{config.type}</td>
                          <td className="py-4 px-4 font-mono text-stone-700">{config.carpet_area}</td>
                          <td className="py-4 px-4 font-mono text-stone-900">{config.price}</td>
                          <td className="py-4 px-4">
                            <button 
                              className="inline-flex items-center gap-1 text-emerald-700 hover:text-emerald-900 text-sm font-medium"
                              onClick={() => setInquiryModal({ open: true, type: "callback" })}
                            >
                              <Eye className="w-4 h-4" strokeWidth={1.5} />
                              View Floor Plan
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <p className="text-xs text-stone-400 mt-4">
                  * Prices are indicative and subject to change. Please contact our team for the latest pricing.
                </p>
              </TabsContent>

              <TabsContent value="specifications" className="p-6">
                <h3 className="font-display text-xl text-stone-900 mb-6">Specifications</h3>
                
                {/* Floor */}
                <div className="mb-8">
                  <h4 className="font-medium text-stone-900 mb-4 flex items-center gap-2">
                    <Layers className="w-5 h-5 text-emerald-700" strokeWidth={1.5} />
                    Floor
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {specifications.floor.map((spec, i) => (
                      <div key={i} className="flex justify-between p-3 bg-stone-50 rounded-sm">
                        <span className="text-stone-600">{spec.label}</span>
                        <span className="text-stone-900 font-medium">{spec.value}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Fittings */}
                <div className="mb-8">
                  <h4 className="font-medium text-stone-900 mb-4 flex items-center gap-2">
                    <Home className="w-5 h-5 text-emerald-700" strokeWidth={1.5} />
                    Fittings
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {specifications.fittings.map((spec, i) => (
                      <div key={i} className="flex justify-between p-3 bg-stone-50 rounded-sm">
                        <span className="text-stone-600">{spec.label}</span>
                        <span className="text-stone-900 font-medium">{spec.value}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Wall & Ceiling */}
                <div>
                  <h4 className="font-medium text-stone-900 mb-4 flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-emerald-700" strokeWidth={1.5} />
                    Wall & Ceiling
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {specifications.wall_ceiling.map((spec, i) => (
                      <div key={i} className="flex justify-between p-3 bg-stone-50 rounded-sm">
                        <span className="text-stone-600">{spec.label}</span>
                        <span className="text-stone-900 font-medium">{spec.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="amenities" className="p-6">
                <h3 className="font-display text-xl text-stone-900 mb-4">Amenities & Facilities</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {property.amenities.map((amenity, i) => {
                    const Icon = getAmenityIcon(amenity);
                    return (
                      <div key={i} className="flex items-center gap-3 p-3 bg-stone-50 rounded-sm">
                        <Icon className="w-5 h-5 text-emerald-700" strokeWidth={1.5} />
                        <span className="text-stone-700">{amenity}</span>
                      </div>
                    );
                  })}
                </div>
              </TabsContent>

              <TabsContent value="brochure" className="p-6">
                <h3 className="font-display text-xl text-stone-900 mb-4">Project Brochure</h3>
                <div className="bg-stone-50 border border-stone-200 rounded-sm p-8 text-center">
                  <FileText className="w-16 h-16 text-stone-400 mx-auto mb-4" strokeWidth={1} />
                  <h4 className="font-medium text-stone-900 mb-2">{property.name} Brochure</h4>
                  <p className="text-stone-500 text-sm mb-6">
                    Get detailed information about floor plans, specifications, amenities, and pricing.
                  </p>
                  <Button 
                    onClick={() => setInquiryModal({ open: true, type: "callback" })}
                    className="btn-primary inline-flex items-center gap-2"
                    data-testid="download-brochure-btn"
                  >
                    <Download className="w-4 h-4" strokeWidth={1.5} />
                    Request Brochure
                  </Button>
                  <p className="text-xs text-stone-400 mt-4">
                    Fill in your details to receive the brochure on WhatsApp/Email
                  </p>
                </div>
              </TabsContent>

              <TabsContent value="pros-cons" className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-display text-lg text-emerald-700 mb-4 flex items-center gap-2">
                      <Check className="w-5 h-5" strokeWidth={1.5} /> Pros
                    </h4>
                    <ul className="space-y-3">
                      {property.pros.map((pro, i) => (
                        <li key={i} className="flex items-start gap-2 text-stone-600">
                          <Check className="w-4 h-4 text-emerald-600 mt-1 flex-shrink-0" strokeWidth={1.5} />
                          {pro}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-display text-lg text-amber-700 mb-4 flex items-center gap-2">
                      <X className="w-5 h-5" strokeWidth={1.5} /> Cons
                    </h4>
                    <ul className="space-y-3">
                      {property.cons.map((con, i) => (
                        <li key={i} className="flex items-start gap-2 text-stone-600">
                          <X className="w-4 h-4 text-amber-600 mt-1 flex-shrink-0" strokeWidth={1.5} />
                          {con}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </TabsContent>
            </Tabs>

            {/* EMI Calculator */}
            <div className="bg-white p-6 rounded-sm shadow-layered" data-testid="emi-calculator">
              <h3 className="font-display text-xl text-stone-900 mb-4 flex items-center gap-2">
                <Calculator className="w-5 h-5" strokeWidth={1.5} /> EMI Calculator
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div>
                  <label className="text-xs uppercase tracking-widest text-stone-500 mb-2 block">Loan Amount (₹)</label>
                  <input
                    type="range"
                    min={property.price_min * 0.5}
                    max={property.price_max}
                    step={100000}
                    value={emiData.loanAmount}
                    onChange={(e) => calculateEMI(Number(e.target.value), emiData.interestRate, emiData.tenure)}
                    className="w-full"
                  />
                  <p className="font-mono text-lg text-stone-900 mt-2">{formatPrice(emiData.loanAmount)}</p>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-stone-500 mb-2 block">Interest Rate (%)</label>
                  <input
                    type="range"
                    min={6}
                    max={12}
                    step={0.1}
                    value={emiData.interestRate}
                    onChange={(e) => calculateEMI(emiData.loanAmount, Number(e.target.value), emiData.tenure)}
                    className="w-full"
                  />
                  <p className="font-mono text-lg text-stone-900 mt-2">{emiData.interestRate}%</p>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-stone-500 mb-2 block">Tenure (Years)</label>
                  <input
                    type="range"
                    min={60}
                    max={360}
                    step={12}
                    value={emiData.tenure}
                    onChange={(e) => calculateEMI(emiData.loanAmount, emiData.interestRate, Number(e.target.value))}
                    className="w-full"
                  />
                  <p className="font-mono text-lg text-stone-900 mt-2">{emiData.tenure / 12} years</p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 p-4 bg-emerald-50 rounded-sm">
                <div className="text-center">
                  <p className="text-xs uppercase tracking-widest text-emerald-700 mb-1">Monthly EMI</p>
                  <p className="font-mono text-xl text-emerald-900">{formatPrice(emiData.emi)}</p>
                </div>
                <div className="text-center border-x border-emerald-200">
                  <p className="text-xs uppercase tracking-widest text-emerald-700 mb-1">Total Interest</p>
                  <p className="font-mono text-xl text-emerald-900">{formatPrice(emiData.totalInterest)}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs uppercase tracking-widest text-emerald-700 mb-1">Total Payment</p>
                  <p className="font-mono text-xl text-emerald-900">{formatPrice(emiData.totalPayment)}</p>
                </div>
              </div>
            </div>

            {/* Project FAQs */}
            <div className="bg-white p-6 rounded-sm shadow-layered" data-testid="project-faqs">
              <h3 className="font-display text-xl text-stone-900 mb-6 flex items-center gap-2">
                <HelpCircle className="w-5 h-5" strokeWidth={1.5} /> 
                Frequently Asked Questions
              </h3>
              
              <Accordion type="single" collapsible className="space-y-3">
                {projectFAQs.map((faq, i) => (
                  <AccordionItem 
                    key={i} 
                    value={`faq-${i}`}
                    className="border border-stone-100 rounded-sm px-4"
                  >
                    <AccordionTrigger className="text-left font-medium text-stone-900 hover:no-underline py-4 text-sm">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-stone-600 pb-4 text-sm">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>

          {/* Right Column - Sticky CTA */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              {/* Quick Actions */}
              <div className="bg-white p-6 rounded-sm shadow-layered" data-testid="quick-actions">
                <h3 className="font-display text-lg text-stone-900 mb-4">Interested in this property?</h3>

                <div className="space-y-3">
                  <Button
                    onClick={() => setInquiryModal({ open: true, type: "site_visit" })}
                    className="w-full btn-primary flex items-center justify-center gap-2"
                    data-testid="schedule-visit-btn"
                  >
                    <Calendar className="w-4 h-4" strokeWidth={1.5} />
                    Schedule Site Visit
                  </Button>

                  <Button
                    onClick={() => setInquiryModal({ open: true, type: "video_tour" })}
                    className="w-full btn-secondary flex items-center justify-center gap-2"
                    data-testid="video-tour-btn"
                  >
                    <Video className="w-4 h-4" strokeWidth={1.5} />
                    Book Video Tour
                  </Button>

                  <Button
                    onClick={() => setInquiryModal({ open: true, type: "callback" })}
                    className="w-full btn-secondary flex items-center justify-center gap-2"
                    data-testid="callback-btn"
                  >
                    <Phone className="w-4 h-4" strokeWidth={1.5} />
                    Request Callback
                  </Button>

                  <Button
                    onClick={() => setInquiryModal({ open: true, type: "offer" })}
                    className="w-full bg-amber-500 hover:bg-amber-600 text-white flex items-center justify-center gap-2 px-6 py-4"
                    data-testid="offer-btn"
                  >
                    <Tag className="w-4 h-4" strokeWidth={1.5} />
                    Get Today's Offer
                  </Button>
                </div>

                <p className="text-xs text-stone-500 text-center mt-4">
                  Zero brokerage. Direct from builder.
                </p>
              </div>

              {/* Builder Info */}
              <div className="bg-white p-6 rounded-sm shadow-layered">
                <p className="text-xs uppercase tracking-widest text-stone-500 mb-2">Developed By</p>
                <h4 className="font-display text-lg text-stone-900 mb-1">{property.builder}</h4>
                <p className="text-sm text-stone-500">Trusted Developer</p>
              </div>

              {/* Price Summary */}
              <div className="bg-emerald-900 p-6 rounded-sm text-white">
                <p className="text-xs uppercase tracking-widest text-emerald-300 mb-2">Starting Price</p>
                <p className="font-mono text-3xl mb-2">{formatPrice(property.price_min)}</p>
                <p className="text-emerald-100/70 text-sm">onwards</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Inquiry Modal */}
      <InquiryModal
        isOpen={inquiryModal.open}
        onClose={() => setInquiryModal({ open: false, type: "callback" })}
        property={property}
        inquiryType={inquiryModal.type}
      />
    </div>
  );
};

export default PropertyDetailPage;
